import React, { useState } from 'react';
import { Menu } from 'lucide-react';
import { DocsTopBar, type SortOption } from '../../molecules/DocsTopBar';
import { type ViewMode } from '../../molecules/SortLabel';
import { type BreadcrumbItem } from '../../molecules/Breadcrumb';
import { LensePanel, type LenseItem } from '../../organisms/LensePanel';
import { useMobileSidePanel } from '../MainLayout/MainLayout';
import './DocumentsPageLayout.css';

// Backward-compatible types (maps to LenseItem internally)
export interface CategoryItem {
  id: string;
  name: string;
  icon?: React.ReactNode;
  count?: number;
  /** Subcategories (maps to 'children' in LensePanel) */
  subcategories?: SubcategoryItem[];
}

export interface SubcategoryItem {
  id: string;
  name: string;
  count?: number;
}

/** Convert CategoryItem[] to LenseItem[] for LensePanel */
function mapCategoriesToItems(categories: CategoryItem[]): LenseItem[] {
  return categories.map((cat) => ({
    id: cat.id,
    name: cat.name,
    icon: cat.icon,
    count: cat.count,
    children: cat.subcategories?.map((sub) => ({
      id: sub.id,
      name: sub.name,
      count: sub.count,
    })),
  }));
}

export interface DocumentsPageLayoutProps {
  /** Category structure for selector panel */
  categories: CategoryItem[];
  /** Currently selected category ID */
  selectedCategory?: string;
  /** Currently selected subcategory ID */
  selectedSubcategory?: string | null;
  /** Category selection handler */
  onCategorySelect?: (categoryId: string, subcategoryId: string | null) => void;
  /** Current sort option */
  sortBy?: SortOption;
  /** Sort change handler */
  onSortChange?: (sort: SortOption) => void;
  /** Upload button click handler */
  onUploadClick?: () => void;
  /** Page title for mobile header */
  title?: string;
  /** Upload button label */
  uploadLabel?: string;
  /** Label for 'created' sort option */
  sortCreatedLabel?: string;
  /** Label for 'uploaded' sort option */
  sortUploadedLabel?: string;
  /** Document feed content */
  children?: React.ReactNode;

  // New docs view mode props
  /** Number of new documents since last viewed */
  newDocsCount?: number;
  /** Current view mode */
  viewMode?: ViewMode;
  /** View mode change handler */
  onViewModeChange?: (mode: ViewMode) => void;
  /** Label for new docs button */
  newDocsLabel?: string;
  /** Label for "N new received" indicator (use {{count}} for number) */
  newReceivedLabel?: string;
  /** FR-017 #7a — processed documents with no twin link (entry shown only when > 0) */
  unlinkedCount?: number;
  /** Label for the unlinked entry (use {{count}} for number) */
  unlinkedLabel?: string;
  /** Label for back to all button */
  backToAllLabel?: string;
  /** Prefix for dropdown options (e.g., "Ordered by") */
  orderedByPrefix?: string;
  /** Label for document date option in dropdown */
  documentDateOptionLabel?: string;
  /** Label for received date option in dropdown */
  receivedDateOptionLabel?: string;

  // Selection mode props
  /** Whether selection mode is active */
  selectionMode?: boolean;
  /** Handler to enter selection mode */
  onEnterSelectionMode?: () => void;
  /** Handler to exit selection mode */
  onExitSelectionMode?: () => void;
  /** Number of selected documents */
  selectedCount?: number;
  /** Total number of documents (for select all) */
  totalCount?: number;
  /** Handler for select all */
  onSelectAll?: () => void;
  /** Handler for deselect all */
  onDeselectAll?: () => void;
  /** Handler for download selected */
  onDownloadSelected?: () => void;
  /** Handler for delete selected */
  onDeleteSelected?: () => void;
  /** Label for "Select documents" menu item */
  selectDocumentsLabel?: string;
  /** Label for "Cancel selection" menu item */
  cancelSelectionLabel?: string;
  /** Label for "Download selected" menu item */
  downloadSelectedLabel?: string;
  /** Label for "Delete selected" menu item */
  deleteSelectedLabel?: string;
  /** Handler for restore selected */
  onRestoreSelected?: () => void;
  /** Label for "Restore selected" menu item */
  restoreSelectedLabel?: string;
  /** Handler for reprocess selected */
  onReprocessSelected?: () => void;
  /** Label for "Reprocess selected" */
  reprocessSelectedLabel?: string;
  /** Label for selected count in selection bar */
  selectedLabel?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Select all label */
  selectAllLabel?: string;

  // Cross-page select-all props
  /** Whether to show the "select all matching" banner */
  showSelectAllBanner?: boolean;
  /** Handler when user clicks "select all matching" */
  onSelectAllMatching?: () => void;
  /** Whether all matching docs across all pages are selected */
  allMatchingSelected?: boolean;
  /** Total matching documents across all pages */
  totalMatching?: number;
  /** Whether loading all matching IDs */
  loadingAllIds?: boolean;
  /** Label for "Select all {count} matching documents" */
  selectAllMatchingLabel?: string;
  /** Label for "All {count} documents selected" */
  allSelectedConfirmLabel?: string;

  // Show deleted documents props
  /** Whether showing deleted documents is active */
  showDeleted?: boolean;
  /** Handler to toggle showing deleted documents */
  onShowDeletedToggle?: () => void;
  /** Label for "Show deleted documents" menu item */
  showDeletedLabel?: string;
  /** Label for "Hide deleted documents" menu item */
  hideDeletedLabel?: string;

  // Breadcrumb props
  /** Breadcrumb items to display above the TopBar */
  breadcrumbItems?: BreadcrumbItem[];

  // Mobile integration props (for MainLayout integration)
  /** Whether the mobile menu is controlled externally (hides native mobile header) */
  isMobileMenuExternal?: boolean;
  /** External mobile menu open state (only used when isMobileMenuExternal is true) */
  externalMobileMenuOpen?: boolean;
  /** External handler to close mobile menu (only used when isMobileMenuExternal is true) */
  onExternalMobileMenuClose?: () => void;

  // Layout variant
  /**
   * Suppresses the left LensePanel and its corresponding desktop left
   * margin. Used by /documents on the bare starting page (#919) — the tile
   * grid is the navigator, so the panel is redundant. Defaults to false to
   * keep all other consumers (lens-scoped surfaces) unchanged.
   */
  hideLensePanel?: boolean;
  /** Title rendered at the top of the LensePanel (e.g. "Digital Archive"). */
  lensePanelTitle?: string;
  /**
   * Suppresses the ``DocsTopBar`` row (breadcrumb + sort selector +
   * upload button). Used by /documents on the bare starting page —
   * the tile grid IS the navigator, so sort + upload chrome are
   * redundant there. Defaults to false.
   */
  hideTopBar?: boolean;
}

export const DocumentsPageLayout: React.FC<DocumentsPageLayoutProps> = ({
  categories,
  selectedCategory = 'all',
  selectedSubcategory = null,
  onCategorySelect,
  sortBy = 'created',
  onSortChange,
  onUploadClick,
  title = 'Documents',
  uploadLabel,
  sortCreatedLabel,
  sortUploadedLabel,
  children,
  // New docs view mode
  newDocsCount,
  viewMode,
  onViewModeChange,
  newDocsLabel,
  newReceivedLabel,
  unlinkedCount,
  unlinkedLabel,
  backToAllLabel,
  orderedByPrefix,
  documentDateOptionLabel,
  receivedDateOptionLabel,
  // Selection mode props
  selectionMode = false,
  onEnterSelectionMode,
  onExitSelectionMode,
  selectedCount = 0,
  totalCount = 0,
  onSelectAll,
  onDeselectAll,
  onDownloadSelected,
  onDeleteSelected,
  selectDocumentsLabel,
  cancelSelectionLabel,
  downloadSelectedLabel,
  deleteSelectedLabel,
  onRestoreSelected,
  restoreSelectedLabel,
  onReprocessSelected,
  reprocessSelectedLabel,
  selectedLabel,
  cancelLabel,
  selectAllLabel,
  // Cross-page select-all
  showSelectAllBanner,
  onSelectAllMatching,
  allMatchingSelected,
  totalMatching,
  loadingAllIds,
  selectAllMatchingLabel,
  allSelectedConfirmLabel,
  // Show deleted documents
  showDeleted,
  onShowDeletedToggle,
  showDeletedLabel,
  hideDeletedLabel,
  // Breadcrumb
  breadcrumbItems,
  // Mobile integration
  isMobileMenuExternal = false,
  externalMobileMenuOpen = false,
  onExternalMobileMenuClose,
  // Layout variant
  hideLensePanel = false,
  lensePanelTitle,
  hideTopBar = false,
}) => {
  const [internalMobileMenuOpen, setInternalMobileMenuOpen] = useState(false);

  // Try to use MainLayout's mobile side panel context if available
  const mainLayoutContext = useMobileSidePanel();

  // Determine mobile menu state source (priority: context > external props > internal state)
  const useMainLayoutContext = mainLayoutContext !== null;
  const useExternalProps = !useMainLayoutContext && isMobileMenuExternal;

  const mobileMenuOpen = useMainLayoutContext
    ? mainLayoutContext.isOpen
    : useExternalProps
      ? externalMobileMenuOpen
      : internalMobileMenuOpen;

  const closeMobileMenu = useMainLayoutContext
    ? mainLayoutContext.close
    : useExternalProps && onExternalMobileMenuClose
      ? onExternalMobileMenuClose
      : () => setInternalMobileMenuOpen(false);

  const toggleMobileMenu = useMainLayoutContext
    ? () => (mainLayoutContext.isOpen ? mainLayoutContext.close() : mainLayoutContext.open())
    : useExternalProps
      ? () => { /* controlled externally */ }
      : () => setInternalMobileMenuOpen(!internalMobileMenuOpen);

  // Hide native mobile header when inside MainLayout (context is available)
  const hideNativeMobileHeader = useMainLayoutContext || isMobileMenuExternal;

  return (
    <div className={`documents-page-layout${hideLensePanel ? ' documents-page-layout--no-lens' : ''}`}>
      {/* Mobile Header with hamburger menu - hidden when inside MainLayout or controlled externally */}
      {!hideNativeMobileHeader && !hideLensePanel && (
        <div className="documents-page-mobile-header">
          <button className="documents-page-mobile-menu-btn" onClick={toggleMobileMenu}>
            <Menu className="documents-page-mobile-menu-icon" />
          </button>
          <h1 className="documents-page-mobile-title">{title}</h1>
          <div className="documents-page-mobile-spacer" />
        </div>
      )}

      {/* Selector Panel — suppressed on the bare /documents starting page (#919). */}
      {!hideLensePanel && (
        <LensePanel
          title={lensePanelTitle}
          items={mapCategoriesToItems(categories)}
          selectedItem={selectedCategory}
          selectedSubItem={selectedSubcategory}
          onSelect={onCategorySelect}
          isMobileOpen={mobileMenuOpen}
          onMobileClose={closeMobileMenu}
          className="documents-page-layout__selector"
        />
      )}

      {/* Main Content */}
      <div className="documents-page-main">
        {/* Top bar with sort and upload — suppressed on the bare
         * starting page where the tile grid IS the navigator. */}
        {!hideTopBar && (
        <DocsTopBar
          sortBy={sortBy}
          onSortChange={onSortChange}
          onUploadClick={onUploadClick}
          sortCreatedLabel={sortCreatedLabel}
          sortUploadedLabel={sortUploadedLabel}
          uploadLabel={uploadLabel}
          // New docs view mode
          newDocsCount={newDocsCount}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          newDocsLabel={newDocsLabel}
          newReceivedLabel={newReceivedLabel}
          unlinkedCount={unlinkedCount}
          unlinkedLabel={unlinkedLabel}
          backToAllLabel={backToAllLabel}
          orderedByPrefix={orderedByPrefix}
          documentDateOptionLabel={documentDateOptionLabel}
          receivedDateOptionLabel={receivedDateOptionLabel}
          // Selection mode
          selectionMode={selectionMode}
          onEnterSelectionMode={onEnterSelectionMode}
          onExitSelectionMode={onExitSelectionMode}
          selectedCount={selectedCount}
          totalCount={totalCount}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
          onDownloadSelected={onDownloadSelected}
          onDeleteSelected={onDeleteSelected}
          selectDocumentsLabel={selectDocumentsLabel}
          cancelSelectionLabel={cancelSelectionLabel}
          downloadSelectedLabel={downloadSelectedLabel}
          deleteSelectedLabel={deleteSelectedLabel}
          onRestoreSelected={onRestoreSelected}
          restoreSelectedLabel={restoreSelectedLabel}
          onReprocessSelected={onReprocessSelected}
          reprocessSelectedLabel={reprocessSelectedLabel}
          selectedLabel={selectedLabel}
          cancelLabel={cancelLabel}
          selectAllLabel={selectAllLabel}
          // Cross-page select-all
          showSelectAllBanner={showSelectAllBanner}
          onSelectAllMatching={onSelectAllMatching}
          allMatchingSelected={allMatchingSelected}
          totalMatching={totalMatching}
          loadingAllIds={loadingAllIds}
          selectAllMatchingLabel={selectAllMatchingLabel}
          allSelectedConfirmLabel={allSelectedConfirmLabel}
          // Show deleted documents
          showDeleted={showDeleted}
          onShowDeletedToggle={onShowDeletedToggle}
          showDeletedLabel={showDeletedLabel}
          hideDeletedLabel={hideDeletedLabel}
          // Breadcrumb
          breadcrumbItems={breadcrumbItems}
        />
        )}

        {/* Document Feed */}
        <div className="documents-page-content">{children}</div>
      </div>
    </div>
  );
};

export default DocumentsPageLayout;
